import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import API from "./config/API";

const Login = () => {
  const navigate = useNavigate();

  const [emailId, setEmailId] = useState("");
  const [password, setPassword] = useState("");
  const [roleType, setRoleType] = useState("PG_OWNER");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const roles = [
    {
      value: "PG_OWNER",
      label: "PG Owner",
    },
    {
      value: "APP_EXECUTOR",
      label: "App Executor",
    },
    {
      value: "USER",
      label: "User",
    },
  ];

  // =========================================================
  // ROLE BUTTON CLICK
  // =========================================================
  const handleRoleClick = (role) => {
    setErrorMessage("");
    setSuccessMessage("");

    // USER -> Directly open User Login page
    if (role.value === "USER") {
      navigate("/userLogin");
      return;
    }

    // PG OWNER / APP EXECUTOR
    setRoleType(role.value);
  };

  // =========================================================
  // LOGIN
  // =========================================================
  const handleLogin = async (e) => {
    e.preventDefault();

    setErrorMessage("");
    setSuccessMessage("");

    if (!emailId.trim()) {
      setErrorMessage("Please enter your email address.");
      return;
    }

    if (!password) {
      setErrorMessage("Please enter your password.");
      return;
    }

    try {
      setLoading(true);

      const request = {
        emailId: emailId.trim(),
        password: password,
        roleType: roleType,
      };

      const response = await axios.post(
        `${API.V2}/pgOwner/loginPgOwner`,
        request,
      );

      if (response?.data?.statusCode === 200) {
        const userData = response.data.data;

        const jwtToken = userData?.jwtToken?.jwtToken || "";

        const expiresIn = userData?.jwtToken?.expiresIn || 0;

        const user = {
          emailId: userData?.emailId || "",
          jwtToken: jwtToken,
          expiresIn: expiresIn,
          pgName: userData?.pgName || "",
          roleType: userData?.roleType || roleType,
          userId: userData?.userId || null,
          userName: userData?.userName || "",
        };

        // =====================================================
        // SAVE USER DETAILS
        // =====================================================

        localStorage.setItem("user", JSON.stringify(user));

        localStorage.setItem("jwtToken", jwtToken);

        localStorage.setItem("emailId", userData?.emailId || "");

        localStorage.setItem("pgName", userData?.pgName || "");

        localStorage.setItem("roleType", userData?.roleType || roleType);

        localStorage.setItem("userId", userData?.userId || "");

        localStorage.setItem("userName", userData?.userName || "");

        setSuccessMessage("Login successful. Redirecting...");

        // =====================================================
        // ROLE BASED NAVIGATION
        // =====================================================

        const loggedInRole = userData?.roleType || roleType;

        setTimeout(() => {
          switch (loggedInRole) {
            case "PG_OWNER":
              navigate("/dashboard");
              break;

            case "APP_ADMIN":
              navigate("/applicationHeadDashboard");
              break;

            case "APP_EXECUTOR":
              navigate("/appExecutorDashboard");
              break;

            case "USER":
              navigate("/usersDashboardPage");
              break;

            default:
              navigate("/");
              break;
          }
        }, 500);
      } else {
        setErrorMessage(
          response?.data?.message || "Invalid email or password.",
        );
      }
    } catch (error) {
      console.error("Login Error:", error);

      if (error?.response?.status === 401) {
        localStorage.clear();

        setErrorMessage(
          error?.response?.data?.message || "Invalid email or password.",
        );
      } else {
        setErrorMessage(
          error?.response?.data?.message ||
            "Unable to login. Please try again.",
        );
      }
    } finally {
      setLoading(false);
    }
  };

  // =========================================================
  // FORGOT PASSWORD
  // =========================================================
  const handleForgotPassword = () => {
    navigate("/forgotPassword");
  };

  // =========================================================
  // REGISTRATION
  // =========================================================
  const handleRegistration = () => {
    navigate("/pgOwnerRegistration");
  };

  return (
    <>
      <div className="login-page">
        <div className="login-container">
          {/* =================================================
              LEFT SIDE - SVK PG BRANDING
          ================================================== */}

          <div className="branding-section">
            <div className="branding-content">
              <div className="logo-circle">SVK</div>

              <h1>SVK PG</h1>

              <p className="branding-subtitle">PG Management System</p>

              <div className="branding-line"></div>

              <p className="branding-description">
                Manage your PG operations, residents and bookings easily from
                one place.
              </p>

              <div className="feature-list">
                <div className="feature-item">
                  <span className="feature-icon">✓</span>
                  <span>Easy PG Management</span>
                </div>

                <div className="feature-item">
                  <span className="feature-icon">✓</span>
                  <span>Room & Bed Management</span>
                </div>

                <div className="feature-item">
                  <span className="feature-icon">✓</span>
                  <span>Tenant Management</span>
                </div>

                <div className="feature-item">
                  <span className="feature-icon">✓</span>
                  <span>Secure Payments</span>
                </div>
              </div>
            </div>
          </div>

          {/* =================================================
              RIGHT SIDE - LOGIN
          ================================================== */}

          <div className="login-section">
            <div className="login-form-container">
              {/* WELCOME BACK MOVED TO RIGHT SIDE */}

              <div className="welcome-section">
                <h2>Welcome Back</h2>

                <p>Please login to your account</p>
              </div>

              {/* =================================================
                  ROLE BUTTONS
              ================================================== */}

              <div className="role-section">
                <label className="role-label">Login As</label>

                <div className="role-buttons">
                  {roles.map((role) => (
                    <button
                      key={role.value}
                      type="button"
                      className={`role-button ${
                        role.value === "USER" ? "user-role" : ""
                      } ${
                        roleType === role.value && role.value !== "USER"
                          ? "active"
                          : ""
                      }`}
                      onClick={() => handleRoleClick(role)}
                    >
                      {role.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* =================================================
                  ERROR MESSAGE
              ================================================== */}

              {errorMessage && (
                <div className="message error-message">{errorMessage}</div>
              )}

              {/* =================================================
                  SUCCESS MESSAGE
              ================================================== */}

              {successMessage && (
                <div className="message success-message">{successMessage}</div>
              )}

              {/* =================================================
                  LOGIN FORM
              ================================================== */}

              <form onSubmit={handleLogin}>
                {/* EMAIL */}

                <div className="form-group">
                  <label htmlFor="emailId">
                    Email Address/Username/Mobile Number
                  </label>

                  <input
                    id="emailId"
                    type="email"
                    value={emailId}
                    onChange={(e) => setEmailId(e.target.value)}
                    placeholder="Enter your email"
                    autoComplete="email"
                    disabled={loading}
                  />
                </div>

                {/* PASSWORD */}

                <div className="form-group">
                  <label htmlFor="password">Password</label>

                  <div className="password-wrapper">
                    <input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Enter your password"
                      autoComplete="current-password"
                      disabled={loading}
                    />

                    <button
                      type="button"
                      className="password-toggle"
                      onClick={() => setShowPassword(!showPassword)}
                      disabled={loading}
                    >
                      {showPassword ? "🙈" : "👁️"}
                    </button>
                  </div>
                </div>

                {/* FORGOT PASSWORD */}

                <div className="forgot-password-container">
                  <button
                    type="button"
                    className="forgot-password"
                    onClick={handleForgotPassword}
                    disabled={loading}
                  >
                    Forgot Password?
                  </button>
                </div>

                {/* LOGIN BUTTON */}

                <button
                  type="submit"
                  className="login-button"
                  disabled={loading}
                >
                  {loading ? "Logging in..." : "Login"}
                </button>
              </form>

              {/* =================================================
                  PG OWNER REGISTRATION
              ================================================== */}

              {roleType === "PG_OWNER" && (
                <div className="registration-section">
                  <span>Don't have an account?</span>

                  <button
                    type="button"
                    onClick={handleRegistration}
                    disabled={loading}
                  >
                    Register as PG Owner
                  </button>
                </div>
              )}

              {/* =================================================
                  SUPPORT FOOTER
              ================================================== */}

              <div className="support-footer">
                For any support, please contact{" "}
                <strong>SVK Solutions Pvt Ltd</strong> or call{" "}
                <a href="tel:0402548797887" className="support-phone">
                  040-2548797887
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* =====================================================
          CSS
      ====================================================== */}

      <style>{`

        * {
          box-sizing: border-box;
        }

        .login-page {
          min-height: 100vh;
          width: 100%;
          display: flex;
          align-items: center;
          justify-content: center;

          padding: 30px;

          background-image:
            linear-gradient(
              rgba(20, 45, 70, 0.45),
              rgba(20, 45, 70, 0.45)
            ),
            url("https://images.unsplash.com/photo-1520277739336-7bf67edfa768?auto=format&fit=crop&w=2000&q=85");

          background-size: cover;
          background-position: center;
          background-repeat: no-repeat;
        }


        /* =====================================================
           MAIN CONTAINER
        ====================================================== */

        .login-container {
          width: 100%;
          max-width: 1050px;
          min-height: 620px;

          display: grid;
          grid-template-columns: 1fr 1fr;

          background: rgba(255, 255, 255, 0.96);

          border-radius: 24px;

          overflow: hidden;

          box-shadow:
            0 25px 60px
            rgba(0, 0, 0, 0.25);
        }


        /* =====================================================
           LEFT BRANDING
        ====================================================== */

        .branding-section {
          position: relative;

          display: flex;
          align-items: center;
          justify-content: center;

          padding: 50px;

          background:
            linear-gradient(
              145deg,
              rgba(15, 71, 110, 0.98),
              rgba(23, 105, 170, 0.94)
            );

          color: white;
        }


        .branding-content {
          width: 100%;
          max-width: 390px;
        }


        .logo-circle {
          width: 75px;
          height: 75px;

          display: flex;
          align-items: center;
          justify-content: center;

          border-radius: 50%;

          background: white;
          color: #1769aa;

          font-size: 24px;
          font-weight: 800;

          letter-spacing: 1px;

          margin-bottom: 20px;

          box-shadow:
            0 8px 25px
            rgba(0, 0, 0, 0.15);
        }


        .branding-section h1 {
          margin: 0;

          font-size: 42px;
          font-weight: 800;

          letter-spacing: 1px;
        }


        .branding-subtitle {
          margin-top: 8px;

          font-size: 18px;

          opacity: 0.9;
        }


        .branding-line {
          width: 65px;
          height: 4px;

          margin: 25px 0;

          border-radius: 10px;

          background: white;
        }


        .branding-description {
          font-size: 15px;

          line-height: 1.7;

          opacity: 0.88;

          margin-bottom: 30px;
        }


        /* =====================================================
           FEATURES
        ====================================================== */

        .feature-list {
          display: flex;
          flex-direction: column;

          gap: 16px;
        }


        .feature-item {
          display: flex;
          align-items: center;

          gap: 12px;

          font-size: 14px;
        }


        .feature-icon {
          width: 24px;
          height: 24px;

          display: flex;
          align-items: center;
          justify-content: center;

          border-radius: 50%;

          background: rgba(255, 255, 255, 0.18);

          font-size: 13px;
          font-weight: bold;
        }


        /* =====================================================
           RIGHT LOGIN SECTION
        ====================================================== */

        .login-section {
          display: flex;
          align-items: center;
          justify-content: center;

          padding: 45px;
          background: white;
        }


        .login-form-container {
          width: 100%;
          max-width: 410px;
        }


        /* =====================================================
           WELCOME BACK
        ====================================================== */

        .welcome-section {
          text-align: center;

          margin-bottom: 28px;
        }


        .welcome-section h2 {
          margin: 0;

          color: #173b57;

          font-size: 30px;
          font-weight: 750;
        }


        .welcome-section p {
          margin-top: 8px;

          color: #777;

          font-size: 14px;
        }


        /* =====================================================
           ROLE SECTION
        ====================================================== */

        .role-section {
          margin-bottom: 25px;
        }


        .role-label {
          display: block;

          margin-bottom: 10px;

          color: #444;

          font-size: 13px;
          font-weight: 600;
        }


        .role-buttons {
          display: flex;
          gap: 8px;

          width: 100%;
        }


        .role-button {
          flex: 1;

          min-height: 43px;

          padding: 9px 10px;

          border: 1px solid #d8e1e8;

          border-radius: 9px;

          background: white;

          color: #555;

          font-size: 12px;
          font-weight: 600;

          cursor: pointer;

          transition: all 0.2s ease;
        }


        .role-button:hover {
          border-color: #1769aa;

          color: #1769aa;

          background: #f5faff;
        }


        .role-button.active {
          border-color: #1769aa;

          background: #1769aa;

          color: white;

          box-shadow:
            0 5px 15px
            rgba(23, 105, 170, 0.22);
        }


        /* USER BUTTON */

        .role-button.user-role {
          border-color: #1769aa;

          color: #1769aa;

          background: #f4f9fd;
        }


        .role-button.user-role:hover {
          background: #1769aa;

          color: white;

          border-color: #1769aa;

          box-shadow:
            0 5px 15px
            rgba(23, 105, 170, 0.25);
        }


        /* =====================================================
           FORM
        ====================================================== */

        .form-group {
          margin-bottom: 18px;
        }


        .form-group label {
          display: block;

          margin-bottom: 8px;

          color: #444;

          font-size: 13px;
          font-weight: 600;
        }


        .form-group input {
          width: 100%;

          height: 48px;

          padding: 0 14px;

          border: 1px solid #d9e0e6;

          border-radius: 9px;

          outline: none;

          background: #fafcfd;

          color: #333;

          font-size: 14px;

          transition:
            border-color 0.2s ease,
            box-shadow 0.2s ease;
        }


        .form-group input:focus {
          border-color: #1769aa;

          background: white;

          box-shadow:
            0 0 0 3px
            rgba(23, 105, 170, 0.10);
        }


        .form-group input:disabled {
          background: #f2f2f2;

          cursor: not-allowed;
        }


        /* =====================================================
           PASSWORD
        ====================================================== */

        .password-wrapper {
          position: relative;
        }


        .password-wrapper input {
          padding-right: 48px;
        }


        .password-toggle {
          position: absolute;

          right: 12px;
          top: 50%;

          transform: translateY(-50%);

          border: none;

          background: transparent;

          cursor: pointer;

          font-size: 17px;

          padding: 4px;
        }


        /* =====================================================
           FORGOT PASSWORD
        ====================================================== */

        .forgot-password-container {
          display: flex;

          justify-content: flex-end;

          margin-top: -5px;

          margin-bottom: 20px;
        }


        .forgot-password {
          border: none;

          background: transparent;

          color: #1769aa;

          font-size: 12px;
          font-weight: 600;

          cursor: pointer;

          padding: 0;
        }


        .forgot-password:hover {
          text-decoration: underline;
        }


        /* =====================================================
           LOGIN BUTTON
        ====================================================== */

        .login-button {
          width: 100%;

          height: 48px;

          border: none;

          border-radius: 9px;

          background: #1769aa;

          color: white;

          font-size: 14px;
          font-weight: 700;

          cursor: pointer;

          transition:
            transform 0.2s ease,
            box-shadow 0.2s ease,
            background 0.2s ease;
        }


        .login-button:hover {
          background: #12598f;

          transform: translateY(-1px);

          box-shadow:
            0 8px 20px
            rgba(23, 105, 170, 0.25);
        }


        .login-button:disabled {
          opacity: 0.65;

          cursor: not-allowed;

          transform: none;

          box-shadow: none;
        }


        /* =====================================================
           MESSAGES
        ====================================================== */

        .message {
          padding: 11px 13px;

          margin-bottom: 18px;

          border-radius: 8px;

          font-size: 12px;

          line-height: 1.4;
        }


        .error-message {
          background: #fff1f1;

          border: 1px solid #ffd2d2;

          color: #c62828;
        }


        .success-message {
          background: #effaf1;

          border: 1px solid #c8e8cc;

          color: #2e7d32;
        }


        /* =====================================================
           REGISTRATION
        ====================================================== */

        .registration-section {
          display: flex;

          justify-content: center;
          align-items: center;

          gap: 5px;

          margin-top: 20px;

          font-size: 12px;

          color: #777;
        }


        .registration-section button {
          border: none;

          background: transparent;

          color: #1769aa;

          font-size: 12px;
          font-weight: 700;

          cursor: pointer;

          padding: 0;
        }


        .registration-section button:hover {
          text-decoration: underline;
        }


        /* =====================================================
           SUPPORT FOOTER
        ====================================================== */

        .support-footer {
          text-align: center;

          margin-top: 22px;

          padding-top: 15px;

          border-top: 1px solid #eeeeee;

          color: #777;

          font-size: 12px;

          line-height: 1.6;
        }


        .support-footer strong {
          color: #1769aa;

          font-weight: 700;
        }


        .support-phone {
          color: #1769aa;

          font-weight: 700;

          text-decoration: none;

          margin-left: 3px;
        }


        .support-phone:hover {
          text-decoration: underline;
        }


        /* =====================================================
           TABLET
        ====================================================== */

        @media (max-width: 850px) {

          .login-page {
            padding: 20px;
          }


          .login-container {
            max-width: 520px;

            grid-template-columns: 1fr;

            min-height: auto;
          }


          .branding-section {
            display: none;
          }


          .login-section {
            padding: 45px 35px;
          }

        }


        /* =====================================================
           MOBILE
        ====================================================== */

        @media (max-width: 500px) {

          .login-page {
            padding: 12px;
          }


          .login-container {
            border-radius: 16px;
          }


          .login-section {
            padding: 32px 22px;
          }


          .welcome-section h2 {
            font-size: 26px;
          }


          .role-buttons {
            gap: 6px;
          }


          .role-button {
            font-size: 11px;

            padding-left: 5px;
            padding-right: 5px;
          }


          .registration-section {
            flex-direction: column;

            gap: 4px;
          }


          .support-footer {
            font-size: 11px;
          }

        }

      `}</style>
    </>
  );
};

export default Login;
